// Utility to convert ArrayBuffer to Base64
function bufToBase64(buf) {
    return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

// Utility to convert Base64 to ArrayBuffer
// Handles both raw base64 and PEM format
function base64ToBuf(base64) {
    // Remove PEM headers/footers and newlines if present
    const cleanBase64 = base64
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/\s/g, ''); // Remove all whitespace including newlines

    return Uint8Array.from(atob(cleanBase64), c => c.charCodeAt(0));
}

// Derive AES key per request from ephemeral client key + server public key
async function deriveAESKey(serverPublicKeyBase64) {
    // 1️⃣ Import server public key (DER SPKI)
    const serverPublicKey = await crypto.subtle.importKey(
        "spki",
        base64ToBuf(serverPublicKeyBase64),
        { name: "ECDH", namedCurve: "P-256" },
        false,
        []
    );

    // 2️⃣ Generate ephemeral client keypair
    const clientKeyPair = await crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        true,
        ["deriveBits"]
    );

    // 3️⃣ Derive shared secret
    const sharedSecret = await crypto.subtle.deriveBits(
        { name: "ECDH", public: serverPublicKey },
        clientKeyPair.privateKey,
        256
    );

    // 4️⃣ Generate per-request salt (16 bytes)
    const salt = crypto.getRandomValues(new Uint8Array(16));

    // 5️⃣ Import shared secret and derive AES key via HKDF
    const hkdfKey = await crypto.subtle.importKey(
        "raw",
        sharedSecret,
        "HKDF",
        false,
        ["deriveKey"]
    );

    const aesKey = await crypto.subtle.deriveKey(
        {
            name: "HKDF",
            hash: "SHA-256",
            salt,
            info: new TextEncoder().encode("dbparity-secure-v1")
        },
        hkdfKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt"]
    );

    // 6️⃣ Export ephemeral client public key
    const client_pub = await crypto.subtle.exportKey(
        "spki",
        clientKeyPair.publicKey
    );

    return {
        aesKey,
        client_pub: bufToBase64(client_pub),
        salt: bufToBase64(salt)
    };
}

// Encrypt a single field (returns object with iv and ciphertext)
async function encryptField(aesKey, value) {
    // Handle null/undefined/empty values or masked values (asterisks)
    if (value === null || value === undefined || value === '' || value === '•••••') {
        return null;
    }

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(value);

    const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aesKey,
        encoded
    );

    return {
        iv: bufToBase64(iv),
        ciphertext: bufToBase64(ciphertext)
    };
}

/**
 * Prepare encrypted payload for Snowflake connection (matching your exact payload structure)
 * @param {Object} payload - The connection payload from preparePayload()
 * @param {string} serverPublicKey - Base64-encoded server public key
 * @returns {Promise<Object>} Encrypted payload ready to send
 */
export async function prepareConnectionPayload(payload, serverPublicKey) {
    const { aesKey, client_pub, salt } = await deriveAESKey(serverPublicKey);

    // Start with the base payload structure
    const encryptedPayload = {
        name: payload.name,
        account: payload.account,
        user: payload.user,
        role: payload.role,
        warehouse: payload.warehouse,
        database: payload.database,
        database_type: payload.database_type,
        auth_type: payload.auth_type,
        is_tested: payload.is_tested,
        is_connected_successfully: payload.is_connected_successfully,

        // Metadata for decryption
        client_pub,
        salt
    };

    // Encrypt password if present and not null
    if (payload.password !== null && payload.password !== undefined && payload.password !== '') {
        encryptedPayload.password = await encryptField(aesKey, payload.password);
    } else {
        encryptedPayload.password = null;
    }

    // Encrypt private_key_pem if present and not null
    if (payload.private_key_pem !== null && payload.private_key_pem !== undefined && payload.private_key_pem !== '') {
        encryptedPayload.private_key_pem = await encryptField(aesKey, payload.private_key_pem);
    } else {
        encryptedPayload.private_key_pem = null;
    }

    // Encrypt passphrase if present and not null
    if (payload.passphrase !== null && payload.passphrase !== undefined && payload.passphrase !== '') {
        encryptedPayload.passphrase = await encryptField(aesKey, payload.passphrase);
    } else {
        encryptedPayload.passphrase = null;
    }

    return encryptedPayload;
}

export async function prepareRepositoryPat(pat, serverPublicKey) {
    const { aesKey, client_pub, salt } = await deriveAESKey(serverPublicKey);
    let encryptedPat;

    // Encrypt pat if present and not null
    if (pat !== null && pat !== undefined && pat !== '') {
        encryptedPat = await encryptField(aesKey, pat);
    } else {
        encryptedPat = null;
    }
    return { pat: encryptedPat, client_pub, salt };
}